import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ShieldCheck, AlertTriangle, Thermometer, Calendar, ArrowRight, Info } from 'lucide-react';

const SCHEDULE = [
  { vial: '10mg',  weekly: '4 × 2.5mg',  note: 'Starting dose · weeks 1–8',     accent: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
  { vial: '20mg',  weekly: '4 × 5mg',    note: 'After 2 months on 10mg',         accent: 'bg-slate-50 border-slate-200 text-slate-900' },
  { vial: '30mg',  weekly: '4 × 7.5mg',  note: 'After 2 months on 20mg',         accent: 'bg-slate-50 border-slate-200 text-slate-900' },
  { vial: '40mg',  weekly: '4 × 10mg',   note: 'After 2 months on 30mg',         accent: 'bg-slate-50 border-slate-200 text-slate-900' },
  { vial: '60mg',  weekly: '4 × 15mg',   note: 'After 2 months on 40mg · maximum dose', accent: 'bg-red-50 border-red-200 text-red-900' },
];

const DosingPage = () => (
  <main className="max-w-3xl mx-auto px-5 sm:px-6 py-8 sm:py-12" data-testid="dosing-page">
    <div className="flex items-center gap-2 text-xs text-slate-500">
      <Link to="/" className="hover:text-red-700">Home</Link>
      <ChevronRight size={13} />
      <span>Dosing &amp; Safety Guide</span>
    </div>

    <header className="mt-5 flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
        <ShieldCheck size={22} className="text-red-600" />
      </div>
      <div>
        <h1 className="text-[#0a0a0a] font-bold text-3xl sm:text-4xl tracking-tight">Dosing &amp; Safety Guide</h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Recommended titration schedule for Tirzepatide &amp; Retatrutide. Move up slowly, never exceed the maximum, and store correctly. <b className="text-red-600">Read in full before use.</b>
        </p>
      </div>
    </header>

    {/* DOSING SCHEDULE TABLE */}
    <section className="mt-8 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="dosing-schedule">
      <div className="px-5 sm:px-6 py-4 border-b border-slate-100 flex items-center gap-2">
        <Calendar size={16} className="text-red-600" />
        <h2 className="text-lg font-bold text-[#0a0a0a]">Titration schedule</h2>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">
          <tr>
            <th className="text-left px-5 sm:px-6 py-3">Vial</th>
            <th className="text-left px-3 py-3">Weekly dose</th>
            <th className="text-left px-3 sm:px-6 py-3">When</th>
          </tr>
        </thead>
        <tbody>
          {SCHEDULE.map((row, idx) => (
            <tr key={row.vial} className={`border-t border-slate-100 ${idx === SCHEDULE.length - 1 ? 'font-semibold' : ''}`}>
              <td className="px-5 sm:px-6 py-4">
                <span className={`inline-block px-2.5 py-1 rounded-md border text-xs font-mono font-bold ${row.accent}`}>{row.vial}</span>
              </td>
              <td className="px-3 py-4 font-mono text-[#0a0a0a]">{row.weekly}</td>
              <td className="px-3 sm:px-6 py-4 text-slate-600">{row.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-5 sm:px-6 py-4 bg-red-600 text-white flex items-start gap-2 text-sm" data-testid="dosing-max-rule">
        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
        <span className="font-semibold">Do not exceed <span className="font-mono">15mg per week</span> under any circumstances.</span>
      </div>
    </section>

    {/* STORAGE / RECONSTITUTION */}
    <section className="mt-6 grid sm:grid-cols-2 gap-4">
      <article className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <Thermometer size={16} className="text-red-600" />
          <h3 className="text-base font-bold text-[#0a0a0a]">Once reconstituted</h3>
        </div>
        <ul className="mt-3 text-sm text-slate-700 space-y-2 list-disc pl-5 marker:text-red-500">
          <li>Store <b>refrigerated at 2–8&deg;C</b> — do not freeze.</li>
          <li>Use within <b>28–32 days</b>. Discard after this window.</li>
          <li>Reconstitute with <b>bacteriostatic water</b> (BAC water), not regular saline.</li>
          <li>Gently swirl to mix — <b>do not shake</b> (can denature the peptide).</li>
        </ul>
      </article>
      <article className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-red-600" />
          <h3 className="text-base font-bold text-[#0a0a0a]">Injection schedule</h3>
        </div>
        <ul className="mt-3 text-sm text-slate-700 space-y-2 list-disc pl-5 marker:text-red-500">
          <li><b>Weekly</b> injection — same day each week.</li>
          <li>Rotate sites: abdomen, thigh, upper arm.</li>
          <li>Use a fresh <b>insulin syringe</b> each time.</li>
          <li><b>Minimum 2 months</b> at each dose before titrating up.</li>
        </ul>
      </article>
    </section>

    {/* DISCLAIMERS */}
    <section className="mt-6 bg-slate-50 border border-slate-200 rounded-2xl p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <Info size={16} className="text-slate-500" />
        <h3 className="text-base font-bold text-[#0a0a0a]">Things you must understand</h3>
      </div>
      <ul className="mt-3 text-sm text-slate-700 space-y-2 list-disc pl-5 marker:text-slate-400">
        <li>You must be <b>18+</b> to purchase from FAT BUSTERS.</li>
        <li>This guide is general information only. <b>FAT BUSTERS does not provide medical advice</b>. Consult a licensed practitioner before use.</li>
        <li>There are <b>no medical guarantees</b>. Individual results vary. By proceeding you assume all risk.</li>
        <li>Possible side effects include nausea, fatigue, injection-site reactions, and hypoglycaemia. Stop and seek medical advice if you experience anything severe.</li>
        <li>Do not use if pregnant, breastfeeding, or with a personal/family history of medullary thyroid carcinoma or MEN-2.</li>
      </ul>
    </section>

    <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600">
        &larr; Back to shop
      </Link>
      <Link
        to="/calculator"
        className="inline-flex items-center gap-2 px-5 py-3 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg text-sm"
        data-testid="dosing-to-calculator-btn"
      >
        Open Reconstitution Calculator <ArrowRight size={14} />
      </Link>
    </div>
  </main>
);

export default DosingPage;
